import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useUnityBuildStore } from './stores/store';
import { CheckoutForm, Return } from './components/storefront/Checkout';
import { useQuery } from 'react-query';
import { UbAccountService } from './api/UbAccountService';
import { useLocalStorage } from './stores/localStorage';
import { AccountCreateFlow } from './components/storefront/AccountCreateFlow';
import { LandingPageV2 } from './components/storefront/LandingPageV2';
import { FoundersStatement } from './components/storefront/FoundersStatement';
import { DevelopersRoadmap } from './components/storefront/DevelopersRoadmap';
import { ProjectList } from './components/projects/ProjectList';
import MasterProjectView from './components/MasterProjectView';
import { useFetchUser } from './hooks/useFetchUsers';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import LogIn from './components/LogIn';

const AppRoutes = () => {
  const { isLoggedIn, setIsLoggedIn, selectedProject } = useStoreWithEqualityFn(
    useUnityBuildStore,
    state => ({
      isLoggedIn: state.isLoggedIn,
      setIsLoggedIn: state.setIsLoggedIn,
      selectedProject: state.selectedProject,
    }),
    shallow
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user } = useFetchUser();

  // Lightweight wrapper that ensures we have an accountId for checkout
  const CheckoutRoute: React.FC = () => {
    const sessionToken = useLocalStorage.getState().sessionToken;
    const { data, isLoading } = useQuery(
      ['checkout-account', sessionToken],
      async () => {
        const accountResp = await UbAccountService.getAccount();
        return accountResp.account?.accountid || '';
      },
      { enabled: !!sessionToken }
    );

    if (!sessionToken) return <CheckoutForm accountId={''} />;
    if (isLoading) return <CheckoutForm accountId={''} />;
    return <CheckoutForm accountId={data || ''} />;
  };

  useEffect(() => {
    if (!user?.isloggedin) {
      setIsLoggedIn(false);
    }
  }, [user, setIsLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      if (selectedProject?.id && location.pathname !== '/project') {
        navigate('/project');
      } else if (!selectedProject?.id && location.pathname !== '/projects') {
        navigate('/projects');
      }
    } else if (
      !location.pathname.startsWith('/signup') &&
      !location.pathname.startsWith('/help') &&
      !location.pathname.startsWith('/checkout') &&
      !location.pathname.startsWith('/return') &&
      !location.pathname.startsWith('/resources') &&
      !location.pathname.startsWith('/knowledge') &&
      !location.pathname.startsWith('/founders-statement') &&
      !location.pathname.startsWith('/roadmap') &&
      location.pathname !== '/' &&
      location.pathname !== '/login'
    ) {
      navigate('/');
    }
  }, [isLoggedIn, selectedProject, location.pathname, navigate]);

  return (
    <Routes>
      {/* Account creation flow */}
      <Route path="/signup/*" element={<AccountCreateFlow />} />

      {/* Login page */}
      <Route path="/login" element={<LogIn />} />

      {/* Checkout flow */}
      <Route path="/checkout" element={<CheckoutRoute />} />
      <Route path="/return" element={<Return />} />

      {/* Main app routes */}
      <Route path="/projects" element={<ProjectList />} />
      <Route path="/project" element={<MasterProjectView />} />

      {/* Public routes */}
      <Route path="/founders-statement" element={<FoundersStatement />} />
      <Route path="/roadmap" element={<DevelopersRoadmap />} />
      <Route path="/" element={<LandingPageV2 />} />
    </Routes>
  );
};

export default React.memo(AppRoutes);
